import React, { useState } from 'react';
import fp from 'lodash/fp';
import { useQuery } from '@apollo/client';
import { GET_PROPOSAL_REVISIONS_FOR_PENDING_APPROVALS } from 'gql/pendingApprovals';
import { ProTable, Link, DateDisplay } from '../../layout';
import {
  filterByKeyword,
  filterByActiveKey,
  filterByProjectStatus,
  getSelectedForTableSearch,
} from '../../../utils';
import {
  ENUM_PROJECT_STATUS_CONTROL_TYPE,
  VALUE_ENUM_APPLICATION_STATUS,
} from 'constants/form';

const getSelectedStatus = getSelectedForTableSearch([
  'PENDING',
  'REJECTED',
  'APPROVED',
  'VOIDED',
]);
const url = (item) =>
  `/projects/${item.project.id}/proposal-revisions/${item.id}`;
const columns = [
  {
    title: 'Project Code',
    dataIndex: ['project', 'profile', 'projectCode'],
    search: false,
    render: (_, item) => <Link to={url(item)}>{_}</Link>,
  },
  {
    title: 'Project Title',
    dataIndex: ['project', 'title'],
    search: false,
    render: (_, item) => <Link to={url(item)}>{_}</Link>,
  },
  {
    title: 'Project Code / Title',
    key: 'keyWord',
    hideInTable: true,
    dataIndex: 'keyWord',
  },
  {
    title: 'Application Date',
    search: false,
    dataIndex: 'date',
    render: (_, item) => (
      <Link to={url(item)}>
        <DateDisplay date={item.date} />
      </Link>
    ),
  },
  {
    title: 'Highlights of Change',
    dataIndex: 'highlights',
    search: false,
    ellipsis: true,
    render: (_, item) => <Link to={url(item)}>{_}</Link>,
  },
  {
    title: 'Justification',
    dataIndex: 'justification',
    ellipsis: true,
    search: false,
    render: (_, item) => <Link to={url(item)}>{_}</Link>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    valueType: 'select',
    initialValue: 'PENDING',
    valueEnum: VALUE_ENUM_APPLICATION_STATUS,
    render: (_, item) => <Link to={url(item)}>{_}</Link>,
  },
  {
    title: 'Project Status',
    key: 'projectStatus',
    dataIndex: ['project', 'projectStatus', 'controlType'],
    valueType: 'select',
    hideInTable: true,
    initialValue: 'IN_PROGRESS',
    valueEnum: {
      ALL: { text: 'All' },
      [ENUM_PROJECT_STATUS_CONTROL_TYPE.IN_PROGRESS]: { text: 'In Progress' },
      [ENUM_PROJECT_STATUS_CONTROL_TYPE.COMPLETED]: { text: 'Completed' },
      [ENUM_PROJECT_STATUS_CONTROL_TYPE.WITHDRAWN]: { text: 'Withdrawn' },
      [ENUM_PROJECT_STATUS_CONTROL_TYPE.TERMINATED]: { text: 'Terminated' },
    },
  },
];

const PendingProposalRevisionsTable = () => {
  const { refetch } = useQuery(GET_PROPOSAL_REVISIONS_FOR_PENDING_APPROVALS, {
    variables: {
      status: ['PENDING'],
      ignoreNotAuthorised: true,
      pageSize: 1,
    },
    errorPolicy: 'all',
  });

  const [activeKey, setActiveKey] = useState('mine');

  return (
    <ProTable
      params={{
        activeKey,
      }}
      toolbar={{
        menu: {
          type: 'inline',
          activeKey,
          items: [
            {
              key: 'mine',
              label: <span>Need your approval</span>,
            },
            {
              key: 'all',
              label: <span>All</span>,
            },
          ],
          onChange: (key) => {
            setActiveKey(key);
          },
        },
        settings: false,
      }}
      columns={columns}
      request={async (params) => {
        // console.log(params);
        const filterBySelectedKeyword = filterByKeyword(params.keyWord);
        const filterBySelectedActiveKey = filterByActiveKey(activeKey);
        const filterBySelectedProjectStatus = filterByProjectStatus(
          params.projectStatus
        );
        const result = await refetch({
          status: getSelectedStatus(params.status),
          pageSize: 0,
        });
        const dataSource = fp.pipe(
          filterBySelectedKeyword,
          filterBySelectedActiveKey,
          filterBySelectedProjectStatus
        )(result?.data?.proposalRevisions?.filter((e) => !!e));
        return Promise.resolve({
          data: dataSource,
          success: true,
        });
      }}
      rowKey="id"
      search={{
        searchText: 'Search',
        layout: 'vertical',
      }}
      pagination={{
        defaultPageSize: 20,
        showLessItems: true,
      }}
      dateFormatter="string"
    />
  );
};

export default PendingProposalRevisionsTable;
